/**
 * Utility functions for EvaluationTables component
 */

import { TableData } from '../types';

/** Column order for evaluation metrics */
export const COLUMN_ORDER = ['delta [min]', 'MAE', 'MAPE', 'MSE', 'RMSE', 'NRMSE', 'WAPE', 'sMAPE', 'MASE'];

/**
 * Get background color based on value intensity for heatmap visualization
 */
export const getBackgroundColor = (
  value: unknown,
  column: string,
  allValues: number[]
): string => {
  // Skip color mapping for non-numeric values, Model column, or delta column
  if (typeof value !== 'number' || column === 'Model' || column === 'model' || column === 'delta [min]') {
    return 'bg-muted';
  }

  // Get min and max values for this column to normalize
  const numericValues = allValues.filter(v => typeof v === 'number' && !isNaN(v));
  if (numericValues.length === 0) return 'bg-gray-100';

  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);

  // Avoid division by zero
  if (max === min) return 'bg-red-100';

  // Normalize value between 0 and 1
  const normalized = (value - min) / (max - min);

  // Create intensive color mapping - red tones (larger error = more red)
  if (normalized <= 0.1) return 'bg-red-50';
  if (normalized <= 0.2) return 'bg-red-100';
  if (normalized <= 0.3) return 'bg-red-200';
  if (normalized <= 0.4) return 'bg-red-300';
  if (normalized <= 0.5) return 'bg-red-400';
  if (normalized <= 0.6) return 'bg-red-500';
  if (normalized <= 0.7) return 'bg-red-600';
  if (normalized <= 0.8) return 'bg-red-700';
  if (normalized <= 0.9) return 'bg-red-800';
  return 'bg-red-900';
};

/**
 * Convert dfEval data to TableData format
 */
export const convertToTableData = (dfEval: Record<string, unknown>, titleFn?: (featureName: string) => string): TableData[] => {
  const tables: TableData[] = [];

  Object.entries(dfEval).forEach(([featureName, featureData]: [string, unknown]) => {
    if (featureData && typeof featureData === 'object') {
      const featureDataTyped = featureData as Record<string, unknown[]>;
      const numRows = featureDataTyped['delta [min]']?.length || 0;
      const rows = [];

      for (let i = 0; i < numRows; i++) {
        const row: Record<string, unknown> = {};
        COLUMN_ORDER.forEach(col => {
          row[col] = featureDataTyped[col]?.[i] ?? 0;
        });
        rows.push(row);
      }

      tables.push({
        title: titleFn ? titleFn(featureName) : `Model Performance Metrics - ${featureName}`,
        description: 'Evaluation metrics for different time averaging intervals',
        columns: COLUMN_ORDER,
        data: rows
      });
    }
  });

  return tables;
};

/**
 * Generate CSV content from evaluation data
 */
export const generateCSVContent = (
  dfEval: Record<string, unknown>,
  sessionId: string,
  modelType: string
): string => {
  const csvLines: string[] = [];

  // Add header with metadata
  csvLines.push('Evaluation Tables Export');
  csvLines.push(`Session ID: ${sessionId}`);
  csvLines.push(`Model Type: ${modelType}`);
  csvLines.push(`Export Date: ${new Date().toLocaleString()}`);
  csvLines.push('');

  // Process each feature's evaluation data
  Object.entries(dfEval).forEach(([featureName, featureData]: [string, unknown]) => {
    if (featureData && typeof featureData === 'object') {
      csvLines.push('');
      csvLines.push(`${featureName} - Model Performance Metrics`);

      // Add header row
      csvLines.push(COLUMN_ORDER.join(','));

      // Add data rows
      const featureDataTyped = featureData as Record<string, unknown[]>;
      const numRows = featureDataTyped['delta [min]']?.length || 0;
      for (let i = 0; i < numRows; i++) {
        const row = COLUMN_ORDER.map(col => {
          const value = featureDataTyped[col]?.[i] ?? 0;
          return typeof value === 'number' ? value.toFixed(5) : value;
        });
        csvLines.push(row.join(','));
      }
    }
  });

  return csvLines.join('\n');
};

/**
 * Generate CSV content from df_eval_ts (per-timestep evaluation data)
 * Structure: feature_name -> delta -> DataFrame with metrics per timestep
 */
export const generateTsCSVContent = (
  dfEvalTs: Record<string, unknown>,
  sessionId: string,
  modelType: string
): string => {
  const csvLines: string[] = [];

  // Add header with metadata
  csvLines.push('Evaluation Tables (Timestep) Export');
  csvLines.push(`Session ID: ${sessionId}`);
  csvLines.push(`Model Type: ${modelType}`);
  csvLines.push(`Export Date: ${new Date().toLocaleString()}`);
  csvLines.push('');

  const tsColumns = ['Timestep', 'MAE', 'MAPE', 'MSE', 'RMSE', 'NRMSE', 'WAPE', 'sMAPE', 'MASE'];

  // Process each feature's timestep evaluation data
  Object.entries(dfEvalTs).forEach(([featureName, deltaData]: [string, unknown]) => {
    if (deltaData && typeof deltaData === 'object') {
      csvLines.push('');
      csvLines.push(`=== ${featureName} - Per-Timestep Metrics ===`);
      csvLines.push('');

      // Each delta interval has its own DataFrame
      Object.entries(deltaData as Record<string, unknown>).forEach(([delta, metrics]: [string, unknown]) => {
        if (metrics && typeof metrics === 'object') {
          csvLines.push(`Delta: ${delta} min`);
          csvLines.push(tsColumns.join(','));

          const metricsTyped = metrics as Record<string, unknown[]>;
          const numRows = metricsTyped['MAE']?.length || 0;

          for (let i = 0; i < numRows; i++) {
            const row = [
              i + 1, // Timestep (1-indexed)
              ...['MAE', 'MAPE', 'MSE', 'RMSE', 'NRMSE', 'WAPE', 'sMAPE', 'MASE'].map(col => {
                const value = metricsTyped[col]?.[i] ?? 0;
                return typeof value === 'number' ? value.toFixed(5) : value;
              })
            ];
            csvLines.push(row.join(','));
          }
          csvLines.push('');
        }
      });
    }
  });

  return csvLines.join('\n');
};

/**
 * Download content as a file
 */
export const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
